import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  FaCalendarCheck, FaExclamationTriangle, FaUserCheck, FaUserTimes, 
  FaUserFriends, FaSave, FaSearch, FaCheck, FaTimes, FaClock, FaLock, FaMinusCircle
} from "react-icons/fa";

interface AttendanceRecord {
  user_id: number;
  name: string;
  email: string;
  designation: string;
  company_name: string;
  profile: string;
  number: string;
  status: 'Present' | 'Absent' | 'Substituted' | 'Late' | 'NA';
  substitute_name: string;
  substitute_phone: string;
  substitute_company: string;
  notes: string;
  consecutive_absences: number;
  flagged_3_absences: boolean;
  is_locked?: number;
}

interface ChapterEvent {
  id: string | number;
  title: string;
  date: string;
}

const formatCleanTitle = (rawTitle: string): string => {
  if (!rawTitle) return '';
  // 1. Strip HTML tags
  let clean = rawTitle.replace(/<[^>]*>/g, '');
  // 2. Decode common HTML entities
  clean = clean
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'");
  // 3. Normalize spaces & whitespace
  return clean.replace(/\s+/g, ' ').trim();
};

const Attendance: React.FC = () => {
  const [eventsList, setEventsList] = useState<ChapterEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | number | null>(null);
  const [attendanceSheet, setAttendanceSheet] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'FLAGGED' | 'PRESENT' | 'LATE' | 'ABSENT' | 'SUBSTITUTED' | 'NA'>('ALL');
  
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Substitution Modal State
  const [subModalUser, setSubModalUser] = useState<AttendanceRecord | null>(null);
  const [subName, setSubName] = useState('');
  const [subPhone, setSubPhone] = useState('');
  const [subCompany, setSubCompany] = useState('');

  const now = new Date();
  let startYear = now.getFullYear();
  let endYear = startYear + 1;
  if (now.getMonth() < 6) {
    startYear = now.getFullYear() - 1;
    endYear = now.getFullYear();
  }
  const currentSocietyYear = `${startYear}-${endYear}`;

  const [societyYear, setSocietyYear] = useState<string>(currentSocietyYear);

  // Fetch Chapter Events based on Society Year
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/events?society_year=${societyYear}`);
        const evs = res.data.events || [];
        setEventsList(evs);
        const preselected = localStorage.getItem('selected_attendance_event_id');
        if (preselected && evs.some((e: any) => String(e.id) === String(preselected))) {
          setSelectedEventId(preselected);
          localStorage.removeItem('selected_attendance_event_id');
        } else if (evs.length > 0) {
          setSelectedEventId(evs[0].id);
        } else {
          setSelectedEventId(null);
          setAttendanceSheet([]);
        }
      } catch (err) {
        console.error('Error fetching attendance events:', err);
      }
    };
    fetchEvents();
  }, [societyYear]);

  // Fetch Attendance Sheet when selectedEventId changes
  useEffect(() => {
    if (!selectedEventId) return;

    const fetchSheet = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/event-sheet/${selectedEventId}`);
        setAttendanceSheet(res.data.eventSheet || []);
      } catch {
        // Silent
      } finally {
        setLoading(false);
      }
    };
    fetchSheet();
  }, [selectedEventId]);

  // Status Change Handler
  const handleStatusChange = (userId: number, status: 'Present' | 'Absent' | 'Substituted' | 'Late' | 'NA') => {
    setAttendanceSheet(prev => prev.map(rec => {
      if (rec.user_id === userId) {
        if (status === 'Substituted' && !rec.substitute_name) {
          // Open Modal for substitution details
          setSubModalUser(rec);
          setSubName(rec.substitute_name || '');
          setSubPhone(rec.substitute_phone || '');
          setSubCompany(rec.substitute_company || '');
        }
        return { ...rec, status };
      }
      return rec;
    }));
  };

  // Save Substitution Modal Details
  const handleSaveSubstitution = () => {
    if (!subModalUser) return;
    setAttendanceSheet(prev => prev.map(rec => {
      if (rec.user_id === subModalUser.user_id) {
        return {
          ...rec,
          status: 'Substituted',
          substitute_name: subName,
          substitute_phone: subPhone,
          substitute_company: subCompany
        };
      }
      return rec;
    }));
    setSubModalUser(null);
  };

  // Save Batch Attendance Sheet
  const handleSaveSheet = async () => {
    if (!selectedEventId) return;
    try {
      setSaving(true);
      setSaveMessage('');

      await axios.post('https://rmbbackend.rmbjaipur.com/auth/attendance/mark-batch', {
        event_id: selectedEventId,
        records: attendanceSheet
      });

      setSaving(false);
      setSaveMessage('✅ Attendance Sheet Saved Successfully!');
      setTimeout(() => setSaveMessage(''), 5000);

      // Refresh to update streak calculations
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/event-sheet/${selectedEventId}`);
      setAttendanceSheet(res.data.eventSheet || []);
    } catch (err) {
      console.error('Error saving attendance sheet:', err);
      alert('Failed to save attendance sheet.');
      setSaving(false);
    }
  };

  // Filtered Roster
  const filteredRoster = attendanceSheet.filter(rec => {
    const matchesSearch = rec.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          rec.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          rec.email.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === 'FLAGGED') return rec.flagged_3_absences;
    if (filterStatus === 'PRESENT') return rec.status === 'Present';
    if (filterStatus === 'LATE') return rec.status === 'Late';
    if (filterStatus === 'ABSENT') return rec.status === 'Absent';
    if (filterStatus === 'SUBSTITUTED') return rec.status === 'Substituted';
    if (filterStatus === 'NA') return rec.status === 'NA';

    return true;
  });

  const flaggedCount = attendanceSheet.filter(r => r.flagged_3_absences).length;
  const presentCount = attendanceSheet.filter(r => r.status === 'Present').length;
  const lateCount = attendanceSheet.filter(r => r.status === 'Late').length;
  const substitutedCount = attendanceSheet.filter(r => r.status === 'Substituted').length;
  const absentCount = attendanceSheet.filter(r => r.status === 'Absent').length;
  const naCount = attendanceSheet.filter(r => r.status === 'NA').length;

  return (
    <div className="p-6 bg-slate-100 min-h-screen space-y-6">
      {/* Top Header & Event Selection */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A2451] flex items-center gap-2">
            <FaCalendarCheck className="text-[#FDBC12]" /> Chapter Event Attendance & Substitution Manager
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Record member attendance, log substitutes for absent members, and track 3+ consecutive absence streaks.
          </p>
        </div>

        <button
          onClick={handleSaveSheet}
          disabled={saving}
          className="bg-[#0A2451] hover:bg-blue-900 text-white font-extrabold px-5 py-2.5 rounded-xl shadow-md transition flex items-center gap-2 text-xs cursor-pointer disabled:opacity-50"
        >
          <FaSave className="text-[#FDBC12]" /> {saving ? 'Saving Sheet...' : 'Save Attendance Sheet'}
        </button>
      </div>

      {saveMessage && (
        <div className="bg-emerald-100 border border-emerald-400 text-emerald-900 p-3 rounded-xl text-xs font-bold flex items-center gap-2 animate-bounce">
          <FaCheck className="text-emerald-600 text-base" /> {saveMessage}
        </div>
      )}

      {/* ⚠️ 3+ CONTINUOUS ABSENCES ALERT BANNER */}
      {flaggedCount > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-amber-50 p-4 rounded-2xl border-2 border-red-300 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-red-500 text-white p-3 rounded-xl shadow">
              <FaExclamationTriangle size={24} />
            </div>
            <div>
              <h3 className="text-sm font-black text-red-900 flex items-center gap-2">
                ATTENTION: {flaggedCount} MEMBER(S) WITH 3+ CONTINUOUS ABSENCES
              </h3>
              <p className="text-xs text-red-700 mt-0.5 font-medium">
                The members highlighted below have missed 3 or more consecutive chapter events/meetings without substitution. Immediate leadership follow-up is recommended!
              </p>
            </div>
          </div>

          <button
            onClick={() => setFilterStatus('FLAGGED')}
            className="bg-red-600 hover:bg-red-700 text-white text-xs font-black px-4 py-2 rounded-xl shadow transition cursor-pointer flex-shrink-0"
          >
            View Flagged Members ({flaggedCount})
          </button>
        </div>
      )}

      {/* Control Bar: Event Selector, Search & Filters */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-wrap justify-between items-center gap-4">
        {/* Society Year & Event Selector */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-700 uppercase">Society Year:</span>
            <select
              value={societyYear}
              onChange={(e) => setSocietyYear(e.target.value)}
              className="p-2.5 border border-slate-300 rounded-xl text-xs font-black text-[#0A2451] bg-slate-50 focus:ring-2 focus:ring-amber-400 outline-none cursor-pointer shadow-xs"
            >
              <option value={`${startYear + 1}-${endYear + 1}`}>{startYear + 1}-{endYear + 1}</option>
              <option value={`${startYear}-${endYear}`}>{startYear}-{endYear} (Current)</option>
              <option value={`${startYear - 1}-${endYear - 1}`}>{startYear - 1}-{endYear - 1}</option>
              <option value="all">All Time History</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-700 uppercase">Select Event:</span>
            <select
              value={selectedEventId || ''}
              onChange={(e) => setSelectedEventId(e.target.value)}
              className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold text-[#0A2451] bg-amber-50 focus:ring-2 focus:ring-amber-400 outline-none max-w-xs cursor-pointer shadow-xs"
            >
              {eventsList.length === 0 ? (
                <option value="">No Events in {societyYear}</option>
              ) : (
                eventsList.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {formatCleanTitle(ev.title)}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setFilterStatus('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'ALL' ? 'bg-[#0A2451] text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All ({attendanceSheet.length})
          </button>
          <button
            onClick={() => setFilterStatus('PRESENT')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'PRESENT' ? 'bg-emerald-600 text-white shadow' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            Present ({presentCount})
          </button>
          <button
            onClick={() => setFilterStatus('LATE')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'LATE' ? 'bg-amber-500 text-slate-950 shadow' : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
            }`}
          >
            ⏰ Late ({lateCount})
          </button>
          <button
            onClick={() => setFilterStatus('SUBSTITUTED')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'SUBSTITUTED' ? 'bg-indigo-600 text-white shadow' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            Substituted ({substitutedCount})
          </button>
          <button
            onClick={() => setFilterStatus('ABSENT')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'ABSENT' ? 'bg-rose-600 text-white shadow' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
            }`}
          >
            Absent ({absentCount})
          </button>
          <button
            onClick={() => setFilterStatus('NA')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              filterStatus === 'NA' ? 'bg-slate-700 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            ⚪ NA ({naCount})
          </button>
          {flaggedCount > 0 && (
            <button
              onClick={() => setFilterStatus('FLAGGED')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                filterStatus === 'FLAGGED' ? 'bg-red-600 text-white shadow' : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              ⚠️ 3+ Absences ({flaggedCount})
            </button>
          )}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Search member name or firm..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0A2451]"
          />
          <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
        </div>
      </div>

      {/* Attendance Roster Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-11 h-11 rounded-full bg-slate-200 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-1/3" />
                  <div className="h-2 bg-slate-100 rounded w-1/4" />
                </div>
                <div className="h-8 bg-slate-100 rounded-lg w-48" />
                <div className="h-6 bg-slate-100 rounded-full w-24" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[700px]">
          <thead>
            <tr className="bg-slate-900 text-white text-[11px] font-extrabold uppercase tracking-wider">
              <th className="p-4">Member Info</th>
              <th className="p-4">Firm &amp; Designation</th>
              <th className="p-4 text-center">Attendance Status</th>
              <th className="p-4">Substitution / Notes</th>
              <th className="p-4 text-center">Absence Streak</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
            {filteredRoster.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-400 font-bold">
                  No member records found matching criteria.
                </td>
              </tr>
            ) : (
              filteredRoster.map(rec => (
                <tr 
                  key={rec.user_id}
                  className={`transition ${
                    rec.flagged_3_absences ? 'bg-red-50/70 hover:bg-red-100/70 border-l-4 border-l-red-500' : 'hover:bg-slate-50'
                  }`}
                >
                  {/* Member Info */}
                  <td className="p-4 flex items-center gap-3">
                    <img
                      src={rec.profile ? (rec.profile.startsWith('http') ? rec.profile : `https://rmbbackend.rmbjaipur.com/${rec.profile}`) : 'https://via.placeholder.com/150'}
                      alt={rec.name}
                      className="w-11 h-11 rounded-full object-cover border-2 border-slate-200 shadow-sm"
                    />
                    <div>
                      <h4 className="font-extrabold text-slate-900 flex items-center gap-1.5">
                        {rec.name}
                        {rec.flagged_3_absences && (
                          <span className="bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase flex items-center gap-1">
                            <FaExclamationTriangle size={10} /> 3+ Absences
                          </span>
                        )}
                      </h4>
                      <p className="text-[11px] text-slate-500 font-normal">{rec.email}</p>
                    </div>
                  </td>

                  {/* Firm & Designation */}
                  <td className="p-4">
                    <div className="font-bold text-slate-800">{rec.company_name || 'RMB Member'}</div>
                    <div className="text-[11px] text-slate-500">{rec.designation || 'Member'}</div>
                  </td>

                  {/* Attendance Status Selector Buttons */}
                  <td className="p-4 text-center">
                    {rec.is_locked ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-300 rounded-xl text-xs font-bold text-slate-700">
                        <FaLock className="text-amber-500" />
                        <span>Locked ({rec.status})</span>
                      </div>
                    ) : (
                      <div className="inline-flex bg-slate-100 p-1 rounded-xl gap-1 border border-slate-200">
                        <button
                          type="button"
                          onClick={() => handleStatusChange(rec.user_id, 'Present')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            rec.status === 'Present' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          <FaUserCheck className="inline mr-1" /> Present
                        </button>

                        <button
                          type="button"
                          onClick={() => handleStatusChange(rec.user_id, 'Late')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            rec.status === 'Late' ? 'bg-amber-500 text-slate-950 shadow font-black' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          <FaClock className="inline mr-1" /> Late
                        </button>

                        <button
                          type="button"
                          onClick={() => handleStatusChange(rec.user_id, 'Substituted')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            rec.status === 'Substituted' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          <FaUserFriends className="inline mr-1" /> Substituted
                        </button>

                        <button
                          type="button"
                          onClick={() => handleStatusChange(rec.user_id, 'Absent')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            rec.status === 'Absent' ? 'bg-rose-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          <FaUserTimes className="inline mr-1" /> Absent
                        </button>

                        <button
                          type="button"
                          onClick={() => handleStatusChange(rec.user_id, 'NA')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            rec.status === 'NA' ? 'bg-slate-700 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                          }`}
                          title="Attendance Not Necessary for this Event"
                        >
                          <FaMinusCircle className="inline mr-1" /> NA
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Substitution / Notes Details */}
                  <td className="p-4">
                    {rec.status === 'Substituted' ? (
                      <div className="bg-indigo-50 border border-indigo-200 p-2 rounded-xl space-y-0.5">
                        <div className="font-extrabold text-indigo-950 flex items-center justify-between">
                          <span>👤 {rec.substitute_name || 'Click to enter substitute'}</span>
                          <button
                            onClick={() => {
                              setSubModalUser(rec);
                              setSubName(rec.substitute_name || '');
                              setSubPhone(rec.substitute_phone || '');
                              setSubCompany(rec.substitute_company || '');
                            }}
                            className="text-[10px] text-indigo-700 underline font-bold"
                          >
                            Edit
                          </button>
                        </div>
                        {rec.substitute_company && (
                          <div className="text-[10px] text-indigo-700 font-semibold">🏢 {rec.substitute_company}</div>
                        )}
                        {rec.substitute_phone && (
                          <div className="text-[10px] text-indigo-700 font-semibold">📞 {rec.substitute_phone}</div>
                        )}
                      </div>
                    ) : rec.status === 'NA' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-extrabold">
                        <FaMinusCircle className="text-slate-500" /> Not Necessary / Exempt
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs italic">
                        {rec.status === 'Present' ? 'Direct Member Attended' : 'No Substitute Recorded'}
                      </span>
                    )}
                  </td>

                  {/* Absence Streak */}
                  <td className="p-4 text-center">
                    {rec.consecutive_absences > 0 ? (
                      <span className={`inline-block font-black text-xs px-2.5 py-1 rounded-full ${
                        rec.consecutive_absences >= 3 ? 'bg-red-600 text-white animate-pulse shadow' : 'bg-amber-100 text-amber-900 border border-amber-300'
                      }`}>
                        {rec.consecutive_absences} Consecutive Absences
                      </span>
                    ) : (
                      <span className="text-emerald-600 font-bold text-xs">Active Streak</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SUBSTITUTION DETAILS MODAL */}
      {subModalUser && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl border border-slate-200 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-base font-black text-[#0A2451] flex items-center gap-2">
                <FaUserFriends className="text-indigo-600" /> Substitute Member Details
              </h3>
              <button 
                onClick={() => setSubModalUser(null)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <FaTimes size={18} />
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Recording substitute attendee for <strong className="text-slate-900">{subModalUser.name}</strong> ({subModalUser.company_name}).
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Substitute Full Name *</label>
                <input
                  type="text"
                  value={subName}
                  onChange={(e) => setSubName(e.target.value)}
                  placeholder="e.g. Rahul Sharma (Representative)"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Substitute Firm / Company</label>
                <input
                  type="text"
                  value={subCompany}
                  onChange={(e) => setSubCompany(e.target.value)}
                  placeholder="e.g. Aadvik Tech Services"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Contact Phone Number</label>
                <input
                  type="text"
                  value={subPhone}
                  onChange={(e) => setSubPhone(e.target.value)}
                  placeholder="e.g. +91 98290 12345"
                  className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                type="button"
                onClick={() => setSubModalUser(null)}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveSubstitution}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow transition cursor-pointer"
              >
                Confirm Substitute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;